import React, { useState, useEffect, useRef } from "react";
import {
  doc, getDoc, setDoc, collection, addDoc, query, where,
  orderBy, limit, onSnapshot, serverTimestamp, updateDoc, getDocs
} from "firebase/firestore";
import { auth, db } from "../firebase";

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400;1,600&family=Jost:wght@200;300;400;500&display=swap');
@import url('https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@latest/tabler-icons.min.css');

*, *::before, *::after { margin:0; padding:0; box-sizing:border-box; }

:root {
  --rose: #C4899A;
  --rose-light: #EDD9DF;
  --rose-dark: #8B5A6A;
  --plum: #83005e;
  --cream: #FAF6F1;
  --ivory: #F5EEE6;
  --warm-white: #FFFCF9;
  --muted: #9A8A85;
  --gold: #C9A96E;
  --gold-light: #F5E6CE;
  --text-dark: #2D1B25;
  --text-mid: #6B5058;
  --border: rgba(196,137,154,0.2);
  --nav-w: 260px;
  --radius: 24px;
}

html, body { height: 100%; background: var(--cream); }
::-webkit-scrollbar { width: 4px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: var(--rose-light); border-radius: 4px; }

.pd-root { display: flex; min-height: 100vh; font-family: 'Jost', sans-serif; background: var(--cream); color: var(--text-dark); position: relative; }

.pd-sidebar {
  width: var(--nav-w);
  background: var(--plum);
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0; left: 0; bottom: 0;
  z-index: 100;
  overflow: hidden;
}

.pd-sidebar::before {
  content: '';
  position: absolute;
  width: 280px; height: 280px;
  border-radius: 50%;
  background: rgba(196,137,154,0.12);
  top: -80px; right: -80px;
  pointer-events: none;
}
.pd-sidebar::after {
  content: '';
  position: absolute;
  width: 200px; height: 200px;
  border-radius: 50%;
  background: rgba(196,137,154,0.08);
  bottom: 80px; left: -60px;
  pointer-events: none;
}

.pd-brand {
  padding: 32px 28px 24px;
  position: relative;
  z-index: 2;
}
.pd-brand-logo {
  width: 48px; height: 48px;
  border-radius: 50%;
  background: white;
  border: 1px solid rgba(0,0,0,0.05);
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 14px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}
.pd-brand-logo i { color: var(--rose-light); font-size: 22px; }
.pd-brand-name {
  font-family: 'Cormorant Garamond', serif;
  font-size: 22px;
  font-weight: 600;
  color: white;
  line-height: 1.1;
  letter-spacing: 0.01em;
}
.pd-brand-sub {
  font-size: 11px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(237,217,223,0.55);
  margin-top: 4px;
}
.pd-sidebar-divider {
  height: 1px;
  background: rgba(255,255,255,0.08);
  margin: 0 28px 20px;
}
.pd-nav {
  flex: 1;
  padding: 0 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  position: relative;
  z-index: 2;
}
.pd-nav-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.22s ease;
  border: none;
  background: transparent;
  width: 100%;
  text-align: left;
  color: rgba(255,255,255,0.55);
  font-family: 'Jost', sans-serif;
  font-size: 13.5px;
  font-weight: 400;
  letter-spacing: 0.01em;
  position: relative;
}
.pd-nav-item:hover { background: rgba(255,255,255,0.07); color: rgba(255,255,255,0.9); }
.pd-nav-item.active {
  background: rgba(196,137,154,0.22);
  color: var(--rose-light);
  font-weight: 500;
}
.pd-nav-item.active::before {
  content: '';
  position: absolute;
  left: 0; top: 50%;
  transform: translateY(-50%);
  width: 3px; height: 20px;
  background: var(--rose);
  border-radius: 0 3px 3px 0;
}
.pd-nav-item i {
  font-size: 18px;
  flex-shrink: 0;
  width: 22px;
  text-align: center;
}
.pd-sidebar-footer {
  padding: 20px 16px 28px;
  position: relative;
  z-index: 2;
}
.pd-logout-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 16px;
  border-radius: 14px;
  cursor: pointer;
  border: 1px solid rgba(255,255,255,0.1);
  background: transparent;
  width: 100%;
  color: rgba(255,255,255,0.45);
  font-family: 'Jost', sans-serif;
  font-size: 13px;
  transition: all 0.2s;
}
.pd-logout-btn:hover {
  background: rgba(255,100,100,0.1);
  border-color: rgba(255,100,100,0.2);
  color: #ff9090;
}
.pd-logout-btn i { font-size: 17px; }

.pd-main {
  margin-left: var(--nav-w);
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}
.pd-topbar {
  background: var(--warm-white);
  border-bottom: 1px solid var(--border);
  padding: 0 40px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: 50;
}
.pd-topbar-title {
  font-family: 'Cormorant Garamond', serif;
  font-size: 22px;
  font-weight: 400;
  color: var(--plum);
  font-style: italic;
}
.pd-topbar-right {
  display: flex;
  align-items: center;
  gap: 16px;
}
.pd-date-badge {
  font-size: 12px;
  color: var(--muted);
  letter-spacing: 0.05em;
  display: flex;
  align-items: center;
  gap: 6px;
}
.pd-date-badge i { font-size: 15px; color: var(--rose); }
.pd-avatar {
  width: 36px; height: 36px;
  border-radius: 50%;
  background: var(--rose-light);
  display: flex; align-items: center; justify-content: center;
  border: 2px solid var(--rose);
}
.pd-avatar i { color: var(--rose-dark); font-size: 18px; }

.pd-content {
  padding: 36px 40px 60px;
  flex: 1;
}
.pd-card {
  background: white;
  border-radius: var(--radius);
  border: 1px solid var(--border);
  overflow: hidden;
  box-shadow: 0 2px 24px rgba(139,90,106,0.06);
}
.pd-section-heading {
  font-family: 'Cormorant Garamond', serif;
  font-size: 34px;
  font-weight: 400;
  color: var(--plum);
  letter-spacing: -0.01em;
  margin-bottom: 6px;
}
.pd-section-heading em { font-style: italic; color: var(--rose-dark); }
.pd-section-sub {
  font-size: 13.5px;
  color: var(--muted);
  font-weight: 300;
  margin-bottom: 32px;
}
.pd-welcome-hero {
  background: linear-gradient(135deg, var(--plum) 0%, #5a003f 100%);
  border-radius: var(--radius);
  padding: 48px 56px;
  position: relative;
  overflow: hidden;
  margin-bottom: 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.pd-welcome-hero::before {
  content: '';
  position: absolute;
  width: 400px; height: 400px;
  border-radius: 50%;
  background: rgba(196,137,154,0.12);
  top: -120px; right: -100px;
  pointer-events: none;
}
.pd-welcome-hero::after {
  content: '';
  position: absolute;
  width: 240px; height: 240px;
  border-radius: 50%;
  background: rgba(196,137,154,0.08);
  bottom: -80px; left: 200px;
  pointer-events: none;
}
.pd-welcome-text { position: relative; z-index: 2; }
.pd-welcome-greeting {
  font-size: 12px;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: rgba(237,217,223,0.65);
  margin-bottom: 12px;
}
.pd-welcome-name {
  font-family: 'Cormorant Garamond', serif;
  font-size: 42px;
  font-weight: 300;
  color: white;
  line-height: 1.1;
  margin-bottom: 14px;
}
.pd-welcome-name em { font-style: italic; color: var(--rose-light); }
.pd-welcome-msg {
  font-size: 14px;
  color: rgba(255,255,255,0.6);
  line-height: 1.7;
  max-width: 380px;
  font-weight: 300;
}
.pd-welcome-icon-wrap { position: relative; z-index: 2; flex-shrink: 0; }
.pd-welcome-circle {
  width: 120px; height: 120px;
  border-radius: 50%;
  background: white;
  border: 1px solid rgba(237,217,223,0.5);
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 6px 14px rgba(0,0,0,0.08);
}
.pd-welcome-circle i { font-size: 52px; color: var(--rose-light); }

.pd-stats-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 32px;
}
.pd-stat-card {
  background: white;
  border-radius: 20px;
  border: 1px solid var(--border);
  padding: 24px 26px;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  transition: transform 0.2s, box-shadow 0.2s;
  cursor: default;
}
.pd-stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 32px rgba(139,90,106,0.1);
}
.pd-stat-icon {
  width: 46px; height: 46px;
  border-radius: 14px;
  display: flex; align-items: center; justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}
.pd-stat-icon.rose { background: var(--rose-light); color: var(--rose-dark); }
.pd-stat-icon.gold { background: var(--gold-light); color: #9A7A40; }
.pd-stat-icon.plum { background: #f0e0f0; color: var(--plum); }
.pd-stat-label {
  font-size: 11.5px;
  color: var(--muted);
  letter-spacing: 0.05em;
  text-transform: uppercase;
  margin-bottom: 5px;
}
.pd-stat-value {
  font-family: 'Cormorant Garamond', serif;
  font-size: 28px;
  font-weight: 600;
  color: var(--text-dark);
  line-height: 1;
}
.pd-stat-note {
  font-size: 12px;
  color: var(--rose);
  margin-top: 4px;
  font-weight: 300;
}
.pd-tip-card {
  background: linear-gradient(135deg, var(--gold-light), #fdf5e8);
  border-radius: 20px;
  border: 1px solid rgba(201,169,110,0.25);
  padding: 24px 28px;
  display: flex;
  align-items: flex-start;
  gap: 18px;
}
.pd-tip-icon {
  width: 44px; height: 44px;
  border-radius: 12px;
  background: rgba(201,169,110,0.2);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  font-size: 20px;
  color: var(--gold);
}
.pd-tip-title {
  font-size: 11px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--gold);
  font-weight: 500;
  margin-bottom: 6px;
}
.pd-tip-text {
  font-size: 14px;
  color: #6B5B30;
  line-height: 1.7;
  font-weight: 300;
}

.pd-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}
.pd-form-group { display: flex; flex-direction: column; gap: 7px; }
.pd-form-group.full { grid-column: 1 / -1; }
.pd-label {
  font-size: 12px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--rose-dark);
  font-weight: 500;
}
.pd-input {
  padding: 13px 16px;
  border: 1.5px solid var(--border);
  border-radius: 12px;
  background: var(--cream);
  font-family: 'Jost', sans-serif;
  font-size: 14px;
  color: var(--text-dark);
  transition: border-color 0.2s, box-shadow 0.2s;
  outline: none;
  width: 100%;
}
.pd-input:focus {
  border-color: var(--rose);
  box-shadow: 0 0 0 3px rgba(196,137,154,0.12);
  background: white;
}
.pd-save-btn {
  margin-top: 28px;
  background: var(--plum);
  color: white;
  border: none;
  padding: 14px 32px;
  border-radius: 40px;
  cursor: pointer;
  font-family: 'Jost', sans-serif;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.05em;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  transition: background 0.2s, transform 0.15s;
}
.pd-save-btn:hover { background: var(--rose-dark); transform: translateY(-1px); }
.pd-save-btn i { font-size: 16px; }

.pd-sterilet-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}
.pd-countdown {
  background: linear-gradient(135deg, var(--plum), var(--rose-dark));
  border-radius: 20px;
  padding: 32px;
  color: white;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.pd-countdown-label {
  font-size: 11px;
  letter-spacing: 0.25em;
  text-transform: uppercase;
  opacity: 0.65;
}
.pd-countdown-date {
  font-family: 'Cormorant Garamond', serif;
  font-size: 38px;
  font-weight: 400;
  line-height: 1.1;
}
.pd-countdown-note {
  font-size: 13px;
  opacity: 0.7;
  font-weight: 300;
  margin-top: 6px;
  line-height: 1.6;
}
.pd-alert-box {
  background: #FFF8F0;
  border: 1.5px solid rgba(201,169,110,0.35);
  border-radius: 16px;
  padding: 18px 20px;
  display: flex;
  gap: 14px;
  align-items: flex-start;
}
.pd-alert-box i { color: var(--gold); font-size: 20px; flex-shrink: 0; margin-top: 1px; }
.pd-alert-text { font-size: 13.5px; color: #6B5030; line-height: 1.65; font-weight: 300; }

.pd-booking-section { margin-top: 32px; }
.pd-booking-title {
  font-family: 'Cormorant Garamond', serif;
  font-size: 26px;
  font-weight: 400;
  color: var(--plum);
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.pd-booking-title i { font-size: 22px; color: var(--rose); }
.pd-booking-sub {
  font-size: 13px;
  color: var(--muted);
  font-weight: 300;
  margin-bottom: 20px;
}
.pd-booking-card {
  background: white;
  border-radius: var(--radius);
  border: 1px solid var(--border);
  padding: 32px 36px;
  box-shadow: 0 2px 24px rgba(139,90,106,0.06);
}
.pd-booking-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  margin-bottom: 20px;
}
.pd-booking-confirmed {
  background: linear-gradient(135deg, #f0fff4, #e8f5e9);
  border: 1.5px solid rgba(92,184,92,0.3);
  border-radius: 16px;
  padding: 20px 24px;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-top: 20px;
  animation: fadeSlideUp 0.35s ease both;
}
.pd-booking-confirmed i { color: #5cb85c; font-size: 22px; flex-shrink: 0; margin-top: 2px; }
.pd-booking-confirmed-text { font-size: 14px; color: #2E7D32; line-height: 1.65; }
.pd-booking-confirmed-text strong { display: block; margin-bottom: 4px; font-size: 15px; }
.pd-book-btn {
  background: linear-gradient(105deg, var(--plum) 0%, #a0005e 100%);
  color: white;
  border: none;
  padding: 14px 32px;
  border-radius: 40px;
  cursor: pointer;
  font-family: 'Jost', sans-serif;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.05em;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  transition: all 0.2s;
  box-shadow: 0 4px 14px rgba(131,0,94,0.25);
}
.pd-book-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 20px rgba(131,0,94,0.35);
}
.pd-book-btn i { font-size: 16px; }

.pd-conseils-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 28px;
}
.pd-conseil-item {
  background: white;
  border-radius: 18px;
  border: 1px solid var(--border);
  padding: 24px;
  display: flex;
  gap: 16px;
  align-items: flex-start;
  transition: transform 0.2s, box-shadow 0.2s;
}
.pd-conseil-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 28px rgba(139,90,106,0.09);
}
.pd-conseil-num {
  width: 36px; height: 36px;
  border-radius: 10px;
  background: var(--rose-light);
  display: flex; align-items: center; justify-content: center;
  font-family: 'Cormorant Garamond', serif;
  font-size: 18px;
  font-weight: 600;
  color: var(--rose-dark);
  flex-shrink: 0;
}
.pd-conseil-text { font-size: 13.5px; color: var(--text-mid); line-height: 1.7; font-weight: 300; }
.pd-contraception-list {
  background: white;
  border-radius: var(--radius);
  border: 1px solid var(--border);
  overflow: hidden;
}
.pd-contra-item {
  display: flex;
  align-items: flex-start;
  gap: 18px;
  padding: 20px 28px;
  border-bottom: 1px solid var(--border);
  transition: background 0.18s;
}
.pd-contra-item:last-child { border-bottom: none; }
.pd-contra-item:hover { background: var(--ivory); }
.pd-contra-dot {
  width: 10px; height: 10px;
  border-radius: 50%;
  background: var(--rose);
  flex-shrink: 0;
  margin-top: 5px;
}
.pd-contra-text { font-size: 14px; color: var(--text-mid); line-height: 1.65; font-weight: 300; }
.pd-contra-text strong { color: var(--plum); font-weight: 500; }

.pd-chat-wrap {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 200px);
  max-height: 680px;
  background: white;
  border-radius: var(--radius);
  border: 1px solid var(--border);
  overflow: hidden;
}
.pd-chat-header {
  padding: 20px 28px;
  border-bottom: 1px solid var(--border);
  display: flex;
  align-items: center;
  gap: 14px;
  background: var(--warm-white);
}
.pd-chat-avatar {
  width: 44px; height: 44px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--plum), var(--rose-dark));
  display: flex; align-items: center; justify-content: center;
}
.pd-chat-avatar i { color: white; font-size: 20px; }
.pd-chat-info-name { font-size: 15px; font-weight: 500; color: var(--text-dark); }
.pd-chat-info-status { font-size: 12px; color: var(--muted); display: flex; align-items: center; gap: 6px; }
.pd-online-dot { width: 7px; height: 7px; border-radius: 50%; background: #5cb85c; }
.pd-messages {
  flex: 1;
  overflow-y: auto;
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: var(--cream);
}
.pd-msg {
  display: flex;
  flex-direction: column;
  max-width: 72%;
}
.pd-msg.mine { align-self: flex-end; align-items: flex-end; }
.pd-msg.theirs { align-self: flex-start; align-items: flex-start; }
.pd-msg-bubble {
  padding: 12px 18px;
  border-radius: 18px;
  font-size: 14px;
  line-height: 1.6;
  font-weight: 300;
}
.pd-msg.mine .pd-msg-bubble {
  background: var(--plum);
  color: white;
  border-bottom-right-radius: 4px;
}
.pd-msg.theirs .pd-msg-bubble {
  background: white;
  color: var(--text-dark);
  border: 1px solid var(--border);
  border-bottom-left-radius: 4px;
}
.pd-msg-time {
  font-size: 11px;
  color: var(--muted);
  margin-top: 4px;
  padding: 0 4px;
}
.pd-chat-input-bar {
  padding: 16px 24px;
  border-top: 1px solid var(--border);
  display: flex;
  gap: 12px;
  align-items: center;
  background: white;
}
.pd-chat-input {
  flex: 1;
  padding: 12px 20px;
  border: 1.5px solid var(--border);
  border-radius: 40px;
  background: var(--cream);
  font-family: 'Jost', sans-serif;
  font-size: 14px;
  outline: none;
  color: var(--text-dark);
  transition: border-color 0.2s;
}
.pd-chat-input:focus { border-color: var(--rose); background: white; }
.pd-chat-input::placeholder { color: var(--muted); }
.pd-send-btn {
  width: 44px; height: 44px;
  border-radius: 50%;
  background: var(--plum);
  border: none;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  transition: background 0.2s, transform 0.15s;
}
.pd-send-btn:hover { background: var(--rose-dark); transform: scale(1.05); }
.pd-send-btn i { color: white; font-size: 18px; }

@keyframes fadeSlideUp {
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
}
.pd-animate { animation: fadeSlideUp 0.35s ease both; }
.pd-toast {
  position: fixed;
  bottom: 32px; right: 32px;
  background: var(--plum);
  color: white;
  padding: 14px 24px;
  border-radius: 14px;
  font-size: 14px;
  font-weight: 400;
  display: flex;
  align-items: center;
  gap: 10px;
  z-index: 999;
  box-shadow: 0 8px 32px rgba(131,0,94,0.3);
  animation: fadeSlideUp 0.3s ease;
}
.pd-toast i { font-size: 18px; color: var(--rose-light); }

.pd-mobile-menu-btn {
  display: none;
  position: fixed;
  top: 14px;
  left: 16px;
  z-index: 200;
  width: 38px; height: 38px;
  border-radius: 10px;
  background: var(--plum);
  border: none;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  box-shadow: 0 3px 12px rgba(131,0,94,0.3);
}
.pd-mobile-menu-btn i { color: white; font-size: 20px; }
.pd-sidebar-overlay {
  display: none;
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  z-index: 99;
  backdrop-filter: blur(2px);
}

@media (max-width: 768px) {
  :root { --nav-w: 260px; --radius: 18px; }
  .pd-root { flex-direction: column; }
  .pd-main { margin-left: 0; }
  .pd-sidebar {
    transform: translateX(-100%);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 101;
    width: min(280px, 85vw);
  }
  .pd-sidebar.mobile-open { transform: translateX(0); }
  .pd-sidebar-overlay.mobile-open { display: block; }
  .pd-mobile-menu-btn { display: flex; }
  .pd-topbar { padding: 0 16px 0 64px; height: 56px; }
  .pd-topbar-title { font-size: 17px; }
  .pd-date-badge { display: none; }
  .pd-content { padding: 20px 16px 100px; }
  .pd-welcome-hero { padding: 32px 24px; flex-direction: column; gap: 24px; align-items: flex-start; }
  .pd-welcome-name { font-size: 30px; }
  .pd-stats-row { grid-template-columns: 1fr; }
  .pd-form-grid { grid-template-columns: 1fr; }
  .pd-save-btn, .pd-book-btn { width: 100%; justify-content: center; }
  .pd-sterilet-grid { grid-template-columns: 1fr; }
  .pd-conseils-grid { grid-template-columns: 1fr; }
  .pd-chat-wrap { height: calc(100vh - 160px); }
}
@media (max-width: 380px) {
  .pd-welcome-name { font-size: 26px; }
  .pd-stat-card { padding: 14px 16px; }
}
`;

export default function PatientDashboard({ userEmail, onLogout = () => {} }) {
  const [tab, setTab] = useState("welcome");
  const [toast, setToast] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [medInfo, setMedInfo] = useState({
    fullName: "", age: "", chronicDiseases: "Aucune maladie chronique connue",
    currentTreatments: "Aucun traitement actuel", lastPeriod: "", allergies: "Aucune allergie connue",
    childrenCount: "", contraceptionMethod: "Je n'utilise pas de contraception"
  });
  const [steriletDate, setSteriletDate] = useState("");
  const [nextRappel, setNextRappel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const messagesEnd = useRef(null);
  const [bookingDate, setBookingDate] = useState("");
  const [bookingTime, setBookingTime] = useState("");
  const [bookingReason, setBookingReason] = useState("Contrôle de routine");
  const [bookingConfirmed, setBookingConfirmed] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  
  // ✅ ID FIXE DE LA SAGE-FEMME (rosyhealth@gmail.com)
  const MIDWIFE_ID = "vwH5dYT2mof7F5sjWsIwzBf5ln22";

  const closeSidebar = () => setSidebarOpen(false);
  const handleNavClick = (id) => { setTab(id); setSidebarOpen(false); };

  // Initialisation de l'utilisateur
  useEffect(() => {
    if (!document.getElementById("pd-css")) {
      const s = document.createElement("style");
      s.id = "pd-css";
      s.textContent = CSS;
      document.head.appendChild(s);
    }
    const user = auth.currentUser;
    if (user) {
      setCurrentUserId(user.uid);
      console.log("✅ Utilisateur connecté:", user.uid);
      fetchMedicalInfo(user.uid);
      fetchSteriletInfo(user.uid);
      fetchLastAppointment(user.uid);
    } else {
      console.warn("⚠️ Aucun utilisateur connecté");
    }
  }, []);

  // ✅ Écoute des messages corrigée (récupère TOUS les messages et filtre côté client)
  useEffect(() => {
    if (!currentUserId) return;

    console.log("🟢 Démarrage écoute messages (filtrage client)");

    const q = query(
      collection(db, "chat_messages"),
      orderBy("timestamp", "asc")
    );

    const unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
        const loaded = snapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(msg =>
            (msg.senderId === currentUserId && msg.receiverId === MIDWIFE_ID) ||
            (msg.senderId === MIDWIFE_ID && msg.receiverId === currentUserId)
          );

        setMessages(loaded);
        console.log("📩 Messages chargés (filtrés):", loaded.length);

        for (const msg of loaded) {
          if (msg.receiverId === currentUserId && msg.read === false) {
            try {
              await updateDoc(doc(db, "chat_messages", msg.id), { read: true });
            } catch (err) {
              console.error(err);
            }
          }
        }
      },
      (error) => {
        console.error("❌ Erreur onSnapshot:", error);
        showToast("Erreur de chargement des messages");
      }
    );

    return () => unsubscribe();
  }, [currentUserId, MIDWIFE_ID]);

  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3000); };

  const fetchMedicalInfo = async (uid) => {
    const docRef = doc(db, "patients_medical_info", uid);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) setMedInfo(docSnap.data());
  };
  const fetchSteriletInfo = async (uid) => {
    const docRef = doc(db, "patients_sterilet", uid);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const data = docSnap.data();
      setSteriletDate(data.insertionDate || "");
      if (data.nextCheckDate) setNextRappel(new Date(data.nextCheckDate));
      else if (data.insertionDate) {
        const insertion = new Date(data.insertionDate);
        const next = new Date(insertion);
        next.setMonth(next.getMonth() + 3);
        setNextRappel(next);
      }
    }
  };
  const fetchLastAppointment = async (uid) => {
    const q = query(collection(db, "appointments"), where("patientId", "==", uid), orderBy("createdAt", "desc"), limit(1));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) setBookingConfirmed(snapshot.docs[0].data());
      else setBookingConfirmed(null);
    });
    return unsubscribe;
  };

  const handleSaveMedical = async (e) => {
    e.preventDefault();
    if (!currentUserId) return;
    try {
      await setDoc(doc(db, "patients_medical_info", currentUserId), { ...medInfo, updatedAt: serverTimestamp() });
      showToast("Dossier médical enregistré");
    } catch (err) { showToast("Erreur d'enregistrement"); }
  };
  const handleSterilet = async (e) => {
    e.preventDefault();
    if (!steriletDate || !currentUserId) return;
    const insertion = new Date(steriletDate);
    const next = new Date(insertion);
    next.setMonth(next.getMonth() + 3);
    setNextRappel(next);
    try {
      await setDoc(doc(db, "patients_sterilet", currentUserId), { insertionDate: steriletDate, nextCheckDate: next.toISOString(), updatedAt: serverTimestamp() });
      showToast("Date de pose enregistrée");
    } catch (err) { showToast("Erreur"); }
  };
  const handleBooking = async (e) => {
    e.preventDefault();
    if (!bookingDate || !bookingTime || !currentUserId) return;
    const d = new Date(bookingDate);
    const formattedDate = d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    try {
      await addDoc(collection(db, "appointments"), {
        patientId: currentUserId,
        patientName: medInfo.fullName || "Patiente",
        date: formattedDate,
        rawDate: bookingDate,
        time: bookingTime,
        reason: bookingReason,
        status: "confirmed",
        createdAt: serverTimestamp()
      });
      setBookingConfirmed({ date: formattedDate, time: bookingTime, reason: bookingReason });
      showToast("Rendez-vous confirmé !");
    } catch (err) { showToast("Erreur"); }
  };

  // Envoi de message avec l'ID fixe
  const handleSend = async (e) => {
    e.preventDefault();
    if (!currentUserId) { showToast("❌ Vous n'êtes pas connecté(e)"); return; }
    if (!newMsg.trim()) { showToast("✏️ Veuillez écrire un message"); return; }
    const now = new Date();
    const timeStr = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    const message = {
      senderId: currentUserId,
      receiverId: MIDWIFE_ID,
      text: newMsg.trim(),
      timestamp: serverTimestamp(),
      time: timeStr,
      read: false,
      role: "midwife"
    };
    try {
      await addDoc(collection(db, "chat_messages"), message);
      setNewMsg("");
      showToast("✓ Message envoyé");
      setTimeout(() => messagesEnd.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (error) {
      console.error(error);
      showToast("⚠️ Erreur d'envoi: " + error.message);
    }
  };

  const navItems = [
    { id: "welcome", label: "Accueil", icon: "ti-home-2" },
    { id: "medical", label: "Mon dossier", icon: "ti-clipboard-heart" },
    { id: "sterilet", label: "Suivi stérilet", icon: "ti-refresh-dot" },
    { id: "conseils", label: "Conseils santé", icon: "ti-bulb" },
    { id: "chat", label: "Chat", icon: "ti-message-2-heart" },
  ];
  const today = new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
  const tabTitles = {
    welcome: "Tableau de bord",
    medical: "Mon dossier médical",
    sterilet: "Suivi stérilet",
    conseils: "Conseils santé",
    chat: "Chat avec ma sage-femme",
  };
  const tips = [
    "Vérifiez les fils de votre stérilet après chaque cycle.",
    "Évitez les bains chauds ou les rapports pendant 24 à 48h après la pose.",
    "Des douleurs inhabituelles doivent vous amener à consulter.",
    "Le suivi gynécologique annuel reste nécessaire.",
    "Un stérilet peut être retiré à tout moment."
  ];
  const contraInfo = [
    { title: "Méthodes hormonales", text: "Pilule, implant, anneau vaginal..." },
    { title: "Méthodes barrières", text: "Préservatifs, diaphragme..." },
    { title: "Méthodes naturelles", text: "Observation de la glaire, température..." },
    { title: "Dispositifs intra-utérins", text: "Stérilet cuivre ou hormonal." },
    { title: "Contraception d'urgence", text: "Pilule du lendemain ou stérilet cuivre." }
  ];
  const timeSlots = ["08:00","08:30","09:00","09:30","10:00","10:30","11:00","11:30","14:00","14:30","15:00","15:30","16:00","16:30","17:00"];
  const chronicDiseasesList = [
    "Aucune maladie chronique connue","Hypertension artérielle","Diabète de type 1","Diabète de type 2",
    "Diabète gestationnel","Asthme","Maladie cardiaque","Polyarthrite rhumatoïde","Ostéoporose","Épilepsie",
    "Psoriasis","Maladie de Crohn","Hypothyroïdie","Hyperthyroïdie","Drépanocytose","Sclérose en plaques",
    "Lupus","Migraine chronique","Endométriose","SOPK","Fibromyalgie"
  ];
  const treatmentsList = [
    "Aucun traitement actuel","Paracétamol","Ibuprofène","Aspirine","Oméprazole","Lévothyroxine","Metformine",
    "Amlodipine","Atorvastatine","Losartan","Simvastatine","Amoxicilline","Citalopram","Sertraline",
    "Contraception orale","Vitamines","THS"
  ];
  const allergiesList = [
    "Aucune allergie connue","Pollens","Acariens","Poils d'animaux","Moississures","Lait de vache","Œufs",
    "Poisson","Crustacés","Fruits à coque","Arachide","Gluten","Soja","Latex","Pénicilline","Aspirine","Iode","Sulfamides"
  ];
  const contraceptionList = [
    "Je n'utilise pas de contraception","Pilule combinée","Pilule progestative","Stérilet cuivre","Stérilet hormonal",
    "Implant","Injection","Patch","Anneau vaginal","Préservatif masculin","Préservatif féminin","Diaphragme",
    "Cape cervicale","Éponge","Méthode des jours fixes","Méthode Billings","Température","Retrait","Stérilisation"
  ];

  return (
    <div className="pd-root">
      <button className="pd-mobile-menu-btn" onClick={() => setSidebarOpen(true)}><i className="ti ti-menu-2" /></button>
      <div className={`pd-sidebar-overlay${sidebarOpen ? " mobile-open" : ""}`} onClick={closeSidebar} />
      <aside className={`pd-sidebar${sidebarOpen ? " mobile-open" : ""}`}>
        <div className="pd-brand">
          <div className="pd-brand-logo"><img src="/logo.png" alt="logo" style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }} /></div>
          <div className="pd-brand-name">RosyHealth</div>
          <div className="pd-brand-sub">Santé féminine</div>
        </div>
        <div className="pd-sidebar-divider" />
        <nav className="pd-nav">
          {navItems.map(item => (
            <button key={item.id} className={`pd-nav-item${tab === item.id ? " active" : ""}`} onClick={() => handleNavClick(item.id)}>
              <i className={`ti ${item.icon}`} /> {item.label}
            </button>
          ))}
        </nav>
        <div className="pd-sidebar-footer">
          <button className="pd-logout-btn" onClick={onLogout}><i className="ti ti-logout" /> Déconnexion</button>
        </div>
      </aside>
      <div className="pd-main">
        <div className="pd-topbar">
          <div className="pd-topbar-title">{tabTitles[tab]}</div>
          <div className="pd-topbar-right">
            <div className="pd-date-badge"><i className="ti ti-calendar-event" />{today}</div>
            <div className="pd-avatar"><i className="ti ti-user" /></div>
          </div>
        </div>
        <div className="pd-content pd-animate" key={tab}>
          {tab === "welcome" && (
            <>
              <div className="pd-welcome-hero">
                <div className="pd-welcome-text">
                  <div className="pd-welcome-greeting">Espace personnel</div>
                  <h1 className="pd-welcome-name">Bienvenue,<br /><em>{medInfo.fullName || "chère patiente"}</em> 🌸</h1>
                  <p className="pd-welcome-msg">Nous sommes heureuses de vous accompagner dans votre parcours de santé féminine.</p>
                </div>
                <div className="pd-welcome-icon-wrap"><div className="pd-welcome-circle"><img src="/logo.png" alt="logo" style={{ width: '70px', height: '70px', borderRadius: '50%', objectFit: 'cover' }} /></div></div>
              </div>
              <div className="pd-stats-row">
                <div className="pd-stat-card"><div className="pd-stat-icon rose"><i className="ti ti-calendar-check" /></div><div><div className="pd-stat-label">Prochain RDV</div><div className="pd-stat-value" style={{fontSize:20}}>{bookingConfirmed ? bookingConfirmed.date.split(" ").slice(1,3).join(" ") : nextRappel ? nextRappel.toLocaleDateString("fr-FR",{day:"numeric",month:"short"}) : "—"}</div><div className="pd-stat-note">{bookingConfirmed ? bookingConfirmed.reason : nextRappel ? "Rappel stérilet" : "Aucun"}</div></div></div>
                <div className="pd-stat-card"><div className="pd-stat-icon gold"><i className="ti ti-messages" /></div><div><div className="pd-stat-label">Messages</div><div className="pd-stat-value">{messages.length}</div><div className="pd-stat-note">échanges avec la sage-femme</div></div></div>
                <div className="pd-stat-card"><div className="pd-stat-icon plum"><i className="ti ti-clipboard-list" /></div><div><div className="pd-stat-label">Dossier</div><div className="pd-stat-value" style={{fontSize:20}}>{medInfo.age ? "Complet" : "À remplir"}</div><div className="pd-stat-note" style={{color: medInfo.age ? "#5cb85c" : "var(--rose)"}}>{medInfo.age ? "À jour" : "Manquant"}</div></div></div>
              </div>
              <div className="pd-tip-card"><div className="pd-tip-icon"><i className="ti ti-bulb" /></div><div><div className="pd-tip-title">Conseil du jour</div><div className="pd-tip-text">{tips[new Date().getDate() % tips.length]}</div></div></div>
            </>
          )}
          {tab === "medical" && (
            <>
              <div className="pd-section-heading">Mon dossier <em>médical</em></div>
              <div className="pd-section-sub">Vos informations de santé, confidentielles.</div>
              <div className="pd-card" style={{padding: "36px 40px"}}>
                <form onSubmit={handleSaveMedical}>
                  <div className="pd-form-grid">
                    <div className="pd-form-group full"><label className="pd-label">Nom complet</label><input className="pd-input" value={medInfo.fullName} onChange={e => setMedInfo({...medInfo, fullName: e.target.value})} /></div>
                    <div className="pd-form-group"><label className="pd-label">Âge</label><input className="pd-input" type="number" value={medInfo.age} onChange={e => setMedInfo({...medInfo, age: e.target.value})} /></div>
                    <div className="pd-form-group"><label className="pd-label">Nombre d'enfants</label><input className="pd-input" type="number" value={medInfo.childrenCount} onChange={e => setMedInfo({...medInfo, childrenCount: e.target.value})} /></div>
                    <div className="pd-form-group"><label className="pd-label">Dernières règles</label><input className="pd-input" type="date" value={medInfo.lastPeriod} onChange={e => setMedInfo({...medInfo, lastPeriod: e.target.value})} /></div>
                    <div className="pd-form-group"><label className="pd-label">Maladies chroniques</label><select className="pd-input" value={medInfo.chronicDiseases} onChange={e => setMedInfo({...medInfo, chronicDiseases: e.target.value})}>{chronicDiseasesList.map(opt => <option key={opt}>{opt}</option>)}</select></div>
                    <div className="pd-form-group"><label className="pd-label">Traitements actuels</label><select className="pd-input" value={medInfo.currentTreatments} onChange={e => setMedInfo({...medInfo, currentTreatments: e.target.value})}>{treatmentsList.map(opt => <option key={opt}>{opt}</option>)}</select></div>
                    <div className="pd-form-group full"><label className="pd-label">Allergies</label><select className="pd-input" value={medInfo.allergies} onChange={e => setMedInfo({...medInfo, allergies: e.target.value})}>{allergiesList.map(opt => <option key={opt}>{opt}</option>)}</select></div>
                    <div className="pd-form-group full"><label className="pd-label">Contraception actuelle</label><select className="pd-input" value={medInfo.contraceptionMethod} onChange={e => setMedInfo({...medInfo, contraceptionMethod: e.target.value})}>{contraceptionList.map(opt => <option key={opt}>{opt}</option>)}</select></div>
                  </div>
                  <button type="submit" className="pd-save-btn"><i className="ti ti-device-floppy" /> Enregistrer</button>
                </form>
              </div>
            </>
          )}
          {tab === "sterilet" && (
            <>
              <div className="pd-section-heading">Suivi de mon <em>stérilet</em></div>
              <div className="pd-section-sub">Enregistrez la pose et recevez un rappel.</div>
              <div className="pd-sterilet-grid">
                <div className="pd-card" style={{padding: "32px 36px"}}>
                  <h3 style={{fontFamily:"'Cormorant Garamond',serif", fontSize:22, color:"var(--plum)", marginBottom:24}}>Date de pose</h3>
                  <form onSubmit={handleSterilet}>
                    <div className="pd-form-group" style={{marginBottom:20}}><label className="pd-label">Date de pose</label><input type="date" className="pd-input" value={steriletDate} onChange={e => setSteriletDate(e.target.value)} required /></div>
                    <button type="submit" className="pd-save-btn"><i className="ti ti-calendar-plus" /> Calculer rappel</button>
                  </form>
                </div>
                <div style={{display:"flex", flexDirection:"column", gap:16}}>
                  {nextRappel ? <div className="pd-countdown"><div className="pd-countdown-label">Prochain contrôle</div><div className="pd-countdown-date">{nextRappel.toLocaleDateString("fr-FR")}</div><div className="pd-countdown-note">Prenez rendez-vous avant cette date.</div></div> : <div style={{background:"var(--ivory)", borderRadius:20, padding:32, textAlign:"center", border:"1.5px dashed var(--rose-light)", color:"var(--muted)"}}><i className="ti ti-calendar-question" style={{fontSize:40, display:"block", marginBottom:12}} />Entrez la date de pose</div>}
                  <div className="pd-alert-box"><i className="ti ti-alert-circle" /><div className="pd-alert-text"><strong>Rappels importants</strong>Vérifiez les fils après chaque cycle. Consultez en cas de douleurs.</div></div>
                  <div className="pd-alert-box" style={{borderColor:"rgba(196,137,154,0.3)", background:"#FFF5F8"}}><i className="ti ti-heart-rate-monitor" style={{color:"var(--rose)"}} /><div className="pd-alert-text"><strong>Bon à savoir</strong>Retrait possible à tout moment, fertilité rapide.</div></div>
                </div>
              </div>
              <div className="pd-booking-section">
                <div className="pd-booking-title"><i className="ti ti-calendar-event" />Prendre un rendez-vous</div>
                <div className="pd-booking-sub">Consultation avec votre sage-femme.</div>
                <div className="pd-booking-card">
                  <form onSubmit={handleBooking}>
                    <div className="pd-booking-grid">
                      <div className="pd-form-group"><label className="pd-label">Date</label><input type="date" className="pd-input" value={bookingDate} min={new Date().toISOString().split("T")[0]} onChange={e => setBookingDate(e.target.value)} required /></div>
                      <div className="pd-form-group"><label className="pd-label">Heure</label><select className="pd-input" value={bookingTime} onChange={e => setBookingTime(e.target.value)} required><option value="">-- Créneau --</option>{timeSlots.map(t => <option key={t}>{t}</option>)}</select></div>
                      <div className="pd-form-group" style={{gridColumn:"1 / -1"}}><label className="pd-label">Motif</label><select className="pd-input" value={bookingReason} onChange={e => setBookingReason(e.target.value)}><option>Contrôle routine</option><option>Vérification stérilet</option><option>Douleurs</option><option>Retrait stérilet</option><option>Pose stérilet</option><option>Autre</option></select></div>
                    </div>
                    <button type="submit" className="pd-book-btn"><i className="ti ti-calendar-check" />Confirmer</button>
                  </form>
                  {bookingConfirmed && <div className="pd-booking-confirmed"><i className="ti ti-circle-check-filled" /><div className="pd-booking-confirmed-text"><strong>Confirmé !</strong>Le {bookingConfirmed.date} à {bookingConfirmed.time}<br />Motif : {bookingConfirmed.reason}</div></div>}
                </div>
              </div>
            </>
          )}
          {tab === "conseils" && (
            <>
              <div className="pd-section-heading">Conseils <em>santé</em></div>
              <div className="pd-section-sub">Recommandations pour votre bien-être.</div>
              <div className="pd-conseils-grid">{tips.map((tip,i) => <div key={i} className="pd-conseil-item"><div className="pd-conseil-num">{i+1}</div><div className="pd-conseil-text">{tip}</div></div>)}</div>
              <div><h3 style={{fontFamily:"'Cormorant Garamond',serif", fontSize:26, color:"var(--plum)", marginBottom:16}}>Moyens de contraception</h3></div>
              <div className="pd-contraception-list">{contraInfo.map((item,i) => <div key={i} className="pd-contra-item"><div className="pd-contra-dot" /><div className="pd-contra-text"><strong>{item.title}</strong> — {item.text}</div></div>)}</div>
            </>
          )}
          {tab === "chat" && (
            <>
              <div className="pd-section-heading">Chat <em>médical</em></div>
              <div className="pd-section-sub">Échangez avec votre sage-femme.</div>
              <div className="pd-chat-wrap">
                <div className="pd-chat-header">
                  <div className="pd-chat-avatar"><i className="ti ti-stethoscope" /></div>
                  <div>
                    <div className="pd-chat-info-name">Votre sage-femme</div>
                    <div className="pd-chat-info-status"><div className="pd-online-dot" /> En ligne</div>
                  </div>
                </div>
                <div className="pd-messages">
                  {messages.length === 0 && (
                    <div style={{textAlign:"center", color:"var(--muted)", marginTop:"20px"}}>
                      <i className="ti ti-message-off" style={{fontSize:"40px", opacity:0.3}} />
                      <p>💬 Aucun message. Écrivez à votre sage-femme !</p>
                    </div>
                  )}
                  {messages.map((msg) => {
                    const isMine = msg.senderId === currentUserId;
                    return (
                      <div key={msg.id} className={`pd-msg ${isMine ? "mine" : "theirs"}`}>
                        <div className="pd-msg-bubble">{msg.text}</div>
                        <div className="pd-msg-time">{msg.time}</div>
                      </div>
                    );
                  })}
                  <div ref={messagesEnd} />
                </div>
                <form className="pd-chat-input-bar" onSubmit={handleSend}>
                  <input className="pd-chat-input" value={newMsg} onChange={e => setNewMsg(e.target.value)} placeholder="Écrivez votre message..." />
                  <button type="submit" className="pd-send-btn"><i className="ti ti-send-2" /></button>
                </form>
              </div>
            </>
          )}
        </div>
      </div>
      {toast && <div className="pd-toast"><i className="ti ti-circle-check-filled" />{toast}</div>}
    </div>
  );
}